import { PlatformError } from '@djgg/core';

export type QualityControlledOperationV1 = 'acceptance' | 'provider-dispatch' | 'publication' | 'preview-grant';
export type QualityAdmissionModeV1 = 'off' | 'admins' | 'all';

/** Server-owned policy identity for the first production verified-core@2 rollout. */
export const QUALITY_V2_POLICY_REVISION = 'verified-core-v2-production@1' as const;

export interface QualityPlatformControlV1 {
  schemaVersion: 1;
  revision: number;
  commandId: string;
  policyRevision: string;
  /** Controls only NEW admissions. Capable workers keep draining accepted work. */
  admissionMode: QualityAdmissionModeV1;
  acceptancePaused: boolean;
  providerDispatchPaused: boolean;
  publicationPaused: boolean;
  previewGrantsPaused: boolean;
  reason: string;
  updatedAt: number;
  updatedBy: string;
}

const TEXT = /^[A-Za-z0-9][A-Za-z0-9._:@/ -]{0,255}$/;

export function validateQualityPlatformControlV1(value: QualityPlatformControlV1): QualityPlatformControlV1 {
  if (value.schemaVersion !== 1) throw new PlatformError('validation', 'unsupported quality platform control schema');
  if (!Number.isSafeInteger(value.revision) || value.revision < 1) throw new PlatformError('validation', 'quality control revision must be positive');
  if (!TEXT.test(value.policyRevision)) throw new PlatformError('validation', 'quality control policy revision is invalid');
  if (!/^[A-Za-z0-9][A-Za-z0-9._:-]{0,127}$/.test(value.commandId)) throw new PlatformError('validation', 'quality control command id is invalid');
  if (!TEXT.test(value.updatedBy)) throw new PlatformError('validation', 'quality control actor is invalid');
  if (!Number.isSafeInteger(value.updatedAt) || value.updatedAt < 1) throw new PlatformError('validation', 'quality control timestamp is invalid');
  if (typeof value.reason !== 'string' || value.reason.length > 500) throw new PlatformError('validation', 'quality control reason is invalid');
  if (!['off', 'admins', 'all'].includes(value.admissionMode)) {
    throw new PlatformError('validation', 'quality control admission mode is invalid');
  }
  for (const flag of ['acceptancePaused', 'providerDispatchPaused', 'publicationPaused', 'previewGrantsPaused'] as const) {
    if (typeof value[flag] !== 'boolean') throw new PlatformError('validation', `quality control ${flag} must be boolean`);
  }
  return structuredClone(value);
}

export function assertQualityOperationAllowed(
  controlValue: QualityPlatformControlV1,
  operation: QualityControlledOperationV1,
  expected?: { revision?: number; policyRevision?: string },
): QualityPlatformControlV1 {
  const control = validateQualityPlatformControlV1(controlValue);
  if (expected?.revision !== undefined && control.revision !== expected.revision) {
    throw new PlatformError('conflict', 'quality control revision changed before commit');
  }
  if (expected?.policyRevision !== undefined && control.policyRevision !== expected.policyRevision) {
    throw new PlatformError('conflict', 'quality policy revision changed before commit');
  }
  const paused = operation === 'acceptance' ? control.acceptancePaused
    : operation === 'provider-dispatch' ? control.providerDispatchPaused
      : operation === 'publication' ? control.publicationPaused
        : control.previewGrantsPaused;
  if (paused) throw new PlatformError('unavailable', `${operation} is paused by quality platform control`);
  return control;
}
